import type {getRentUpfrontPricing} from "../../../../utils/rentUpfront";

// shared by the landing page calculator and the portfolio calculator, so a tenancy is
// priced the same way on both
export type AdvancedRentPricing = ReturnType<typeof getRentUpfrontPricing>;

export type Plan = {months: number; rate: number; adminFee: number};

export const toPlans = (pricing: AdvancedRentPricing) => [
  {months: 3, rate: pricing.threeMonthsRate, adminFee: pricing.threeMonthsFee},
  {months: 6, rate: pricing.sixMonthsRate, adminFee: pricing.sixMonthsFee},
  {months: 12, rate: pricing.twelveMonthsRate, adminFee: pricing.twelveMonthsFee},
];

export const calculatePayout = (monthlyRentPennies: number, plan: Plan) => {
  const gross = monthlyRentPennies * plan.months;
  const rateFee = Math.floor(plan.rate * gross);
  const payout = Math.max(gross - rateFee - plan.adminFee, 0);
  return {gross, rateFee, payout};
};

// every tenancy is a separate advance with its own admin fee, so an HMO pays it once per room
export const calculatePortfolioPayout = (monthlyRentsPennies: Array<number>, plan: Plan) =>
  monthlyRentsPennies
    .filter((rent) => rent > 0)
    .reduce(
      (total, rent) => {
        const {gross, rateFee, payout} = calculatePayout(rent, plan);
        return {
          tenancies: total.tenancies + 1,
          monthlyRent: total.monthlyRent + rent,
          gross: total.gross + gross,
          rateFee: total.rateFee + rateFee,
          adminFee: total.adminFee + plan.adminFee,
          payout: total.payout + payout,
        };
      },
      {tenancies: 0, monthlyRent: 0, gross: 0, rateFee: 0, adminFee: 0, payout: 0},
    );
